import { Injectable } from "@angular/core";
import { map } from "lodash";
import { Observable } from "rxjs";
import {
  AssessmentSectionTemplateConfig,
  AssessmentTemplateConfig,
  AssessmentType,
  Lookup,
  TeamRole,
  TemplateRules,
  UserRole,
} from "../master-data-service/master-data-interface";
import { DatabaseProvider } from "../offline-database/db-connection";
import { DbQueries } from "../offline-database/db-queries";

@Injectable()
export class MasterDataManager {
  constructor(public database: DatabaseProvider) {}

  public createUserRole(user: UserRole) {
    const { userId, userRole, tenantId, qbosUserId } = user;

    this.database.getInstance().subscribe(
      (db) => {
        db.transaction(
          (tx) => {
            tx.executeSql(DbQueries.USER_ROLE_INSERT, [
              userId,
              userRole,
              tenantId,
              qbosUserId,
            ]);
          },
          (error) => {
            console.log("user role transaction error: " + error.message);
          },
          () => {
            console.log("user role transaction ok");
          }
        );
      },
      (error) => {
        console.log("createUserRole.getInstance error: " + error.message);
      }
    );
  }

  public createTeamRole(teamRoles: TeamRole[]) {
    this.database.getInstance().subscribe(
      (db) => {
        db.transaction(
          (tx) => {
            map(teamRoles, (role) => {
              const { tenantId, teamRole, roleLookupCode, workGroup } = role;
              tx.executeSql(DbQueries.TEAM_ROLE_INSERT, [
                tenantId,
                teamRole,
                roleLookupCode,
                workGroup,
              ]);
            });
          },
          (error) => {
            console.log("team role transaction error: " + error.message);
          },
          () => {
            console.log("team role transaction ok");
          }
        );
      },
      (error) => {
        console.log("createTeamRole.getInstance error: " + error.message);
      }
    );
  }

  public createLookup(lookupCodes: Lookup[]) {
    this.database.getInstance().subscribe(
      (db) => {
        db.transaction(
          (tx) => {
            map(lookupCodes, (lookup) => {
              const {
                tenantId,
                lookupCode,
                lookupTypeCode,
                lookupValue,
                lookupValue1,
                description,
                sortSequence,
                isDefaultVisible,
                lookupReferenceKey,
              } = lookup;

              tx.executeSql(DbQueries.LOOKUP_INSERT, [
                tenantId,
                lookupCode,
                lookupTypeCode,
                lookupValue,
                lookupValue1,
                description,
                sortSequence,
                isDefaultVisible,
                lookupReferenceKey,
              ]);
            });
          },
          (error) => {
            console.log("Lookup transaction error: " + error.message);
          },
          () => {
            console.log("Lookup transaction ok");
          }
        );
      },
      (error) => {
        console.log("createLookup.getInstance error: " + error.message);
      }
    );
  }

  public createAssessmentType(assessmentTypes: AssessmentType[]): any {
    this.database.getInstance().subscribe(
      (db) => {
        db.transaction(
          (tx) => {
            map(assessmentTypes, (asmtType) => {
              const {
                tenantId,
                assessmentTypeLookupCode,
                levelOfCareLookupCode,
                roleLookupCode,
              } = asmtType;
              tx.executeSql(DbQueries.ASSESSMENT_TYPE_INSERT, [
                tenantId,
                assessmentTypeLookupCode,
                levelOfCareLookupCode,
                roleLookupCode,
              ]);
            });
          },
          (error) => {
            console.log("Assessment Type transaction error: " + error.message);
          },
          () => {
            console.log("Assessment Type transaction ok");
          }
        );
      },
      (error) => {
        console.log("createAssessmentType.getInstance error: " + error.message);
      }
    );
  }

  public clearMasterData(userId: any) {
    return new Observable((observer) => {
      this.database.getInstance().subscribe(
        (db) => {
          db.transaction(
            function (tx) {
              tx.executeSql(
                DbQueries.IS_USER_ROLE_EXISTS,
                [userId],
                function (_, rs) {
                  if (
                    rs.rows.length === 0 ||
                    (rs.rows.length > 0 &&
                      parseInt(rs.rows.item(0).user_id) !== parseInt(userId))
                  ) {
                    tx.executeSql(DbQueries.DELETE_USER_ROLE);
                    tx.executeSql(DbQueries.DELETE_TEAM_ROLE);
                    tx.executeSql(DbQueries.DELETE_LOOKUP);
                    tx.executeSql(DbQueries.DELETE_ASSESSMENT_TYPE);
                    tx.executeSql(DbQueries.DELETE_RULES_TEMPLATE);
                    tx.executeSql(DbQueries.DELETE_ASSESSMENT_TEMPLATE_CONFIG);
                    tx.executeSql(
                      DbQueries.DELETE_ASSESSMENT_SECTION_TEMPLATE_CONFIG
                    );
                    tx.executeSql(DbQueries.DELETE_PERMISSION_DICTIONARY);
                  }
                  observer.next();
                }
              );
            },
            function (error) {
              console.log("clearMasterData error: " + error.message);
              observer.error(error);
            },
            function () {
              console.log("clearMasterData transaction ok");
              observer.complete();
            }
          );
        },
        (error) => {
          console.log("clearMasterData.getInstance error: " + error.message);
          observer.error(error);
        }
      );
    });
  }

  public createRulesTemplate(templateRules: TemplateRules[]) {
    this.database.getInstance().subscribe(
      (db) => {
        db.transaction(
          (tx) => {
            map(templateRules, (tmplRule) => {
              const {
                tenantId,
                tmplPath,
                tmplJson,
                asmtTypeLookupCode,
                roleLookupCode,
              } = tmplRule;
              tx.executeSql(DbQueries.INSERT_RULES_TEMPLATE, [
                tenantId,
                tmplPath,
                tmplJson,
                asmtTypeLookupCode,
                roleLookupCode,
              ]);
            });
          },
          (error) => {
            console.log(
              "createRulesTemplate transaction error: " + error.message
            );
          },
          () => {
            console.log("createRulesTemplate transaction ok");
          }
        );
      },
      (error) => {
        console.log("createRulesTemplate.getInstance error: " + error.message);
      }
    );
  }

  public createAsmtTemplateConfig(tmplConfigs: AssessmentTemplateConfig[]) {
    this.database.getInstance().subscribe(
      (db) => {
        db.transaction(
          (tx) => {
            map(tmplConfigs, (tmplConfig) => {
              const {
                tenantId,
                tmplVersion,
                tmplPath,
                tmplJson,
                asmtTypeLookupCode,
              } = tmplConfig;
              tx.executeSql(DbQueries.INSERT_ASSESSMENT_TEMPLATE_CONFIG, [
                tenantId,
                tmplVersion,
                tmplPath,
                tmplJson,
                asmtTypeLookupCode,
              ]);
            });
          },
          (error) => {
            console.log(
              "createAsmtTemplateConfig transaction error: " + error.message
            );
          },
          () => {
            console.log("createAsmtTemplateConfig transaction ok");
          }
        );
      },
      (error) => {
        console.log(
          "createAsmtTemplateConfig.getInstance error: " + error.message
        );
      }
    );
  }

  public createAsmtSectionTemplateConfig(
    asmtSectionTmplConfig: AssessmentSectionTemplateConfig[]
  ) {
    this.database.getInstance().subscribe(
      (db) => {
        db.transaction(
          (tx) => {
            map(asmtSectionTmplConfig, (sectionTmplConfig) => {
              const {
                tenantId,
                tmplVersion,
                tmplPath,
                tmplJson,
                asmtTypeLookupCode,
                sectionTypeLookupCode,
              } = sectionTmplConfig;
              tx.executeSql(
                DbQueries.INSERT_ASSESSMENT_SECTION_TEMPLATE_CONFIG,
                [
                  tenantId,
                  tmplVersion,
                  tmplPath,
                  tmplJson,
                  asmtTypeLookupCode,
                  sectionTypeLookupCode,
                ]
              );
            });
          },
          (error) => {
            console.log(
              "createAsmtSectionTemplateConfig transaction error: " +
                error.message
            );
          },
          () => {
            console.log("createAsmtSectionTemplateConfig transaction ok");
          }
        );
      },
      (error) => {
        console.log(
          "createAsmtSectionTemplateConfig.getInstance error: " + error.message
        );
      }
    );
  }

  public createPermissionDictionary(permDictionary) {
    this.database.getInstance().subscribe(
      (db) => {
        db.transaction(
          (tx) => {
            map(permDictionary, (permDict) => {
              const {
                groupId,
                workgroup,
                accessLookupCode,
                accessLookupValue,
              } = permDict;
              tx.executeSql(DbQueries.PERMISSION_DICTIONARY_INSERT, [
                Number(groupId),
                workgroup,
                accessLookupCode,
                accessLookupValue,
              ]);
            });
          },
          (error) => {
            console.log(
              "createPermissionDictionary transaction error: ",
              error.message
            );
          },
          () => {
            console.log("createPermissionDictionary transaction ok");
          }
        );
      },
      (error) => {
        console.log(
          "createPermissionDictionary.getInstance error: ",
          error.message
        );
      }
    );
  }
}



// WEBPACK FOOTER //
// ./src/providers/offline-manager/master-data-manager.ts